import { c as _c } from "react/compiler-runtime";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@opensea/next-ui-kit";
import { Flex, FlexCenter, FlexColumn, Form, Input, Select, SelectItem, SelectItemContent, SelectItemTitle, SkeletonLine, TextBody } from "@opensea/ui-kit";
import { useTranslations } from "next-intl";
import { useEffect } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { withSuspense } from "@/components/suspense/WithSuspense";
import { QUERY_PARAM_KEYS } from "@/hooks/query-param/keys";
import { isFormSubmitDisabled } from "@/lib/form";
import { usePriceQueryParam } from "../../../hooks/query-param/use-price-query-param";
import { FilterPanel } from "../FilterPanel";
import { pricePanelSchema } from "./schema";
export function PricePanel(t0) {
  const $ = _c(11);
  const {
    title,
    queryParam,
    defaultCurrency,
    currencyOptions,
    nativeCurrency,
    minimumPrice,
    defaultOpen,
    disabled,
    open,
    onOpenChange
  } = t0;
  let t1;
  if ($[0] !== currencyOptions || $[1] !== defaultCurrency || $[2] !== defaultOpen || $[3] !== disabled || $[4] !== minimumPrice || $[5] !== nativeCurrency || $[6] !== onOpenChange || $[7] !== open || $[8] !== queryParam || $[9] !== title) {
    t1 = <PricePanelView currencyOptions={currencyOptions} defaultCurrency={defaultCurrency} defaultOpen={defaultOpen} disabled={disabled} minimumPrice={minimumPrice} nativeCurrency={nativeCurrency} onOpenChange={onOpenChange} open={open} queryParam={queryParam} title={title} />;
    $[0] = currencyOptions;
    $[1] = defaultCurrency;
    $[2] = defaultOpen;
    $[3] = disabled;
    $[4] = minimumPrice;
    $[5] = nativeCurrency;
    $[6] = onOpenChange;
    $[7] = open;
    $[8] = queryParam;
    $[9] = title;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  return t1;
}
const PricePanelView = withSuspense(function PricePanelView({
  title,
  queryParam = QUERY_PARAM_KEYS.price,
  defaultCurrency = "ETH",
  currencyOptions = ["ETH", "WETH"],
  nativeCurrency = "ETH",
  minimumPrice,
  defaultOpen,
  disabled,
  open,
  onOpenChange
}) {
  const t = useTranslations("PricePanel");
  const {
    price,
    setPrice
  } = usePriceQueryParam(queryParam);
  const {
    control,
    register,
    handleSubmit,
    formState,
    setValue,
    trigger
  } = useForm({
    defaultValues: {
      currency: price?.currency ?? defaultCurrency,
      min: price?.min ?? undefined,
      max: price?.max ?? undefined,
      rangeMin: minimumPrice
    },
    resolver: zodResolver(pricePanelSchema),
    mode: "onChange"
  });
  const onSubmit = handleSubmit(async data => {
    await setPrice({
      min: data.min,
      max: data.max,
      currency: data.currency,
      nativeCurrency
    });
  });
  useEffect(() => {
    setValue("min", price?.min);
    setValue("max", price?.max);
  }, [price, setValue]);
  const {
    errors
  } = formState;
  const min = useWatch({
    control,
    name: "min"
  });
  const max = useWatch({
    control,
    name: "max"
  });
  useEffect(() => {
    void trigger(["min", "max"]);
  }, [min, max, trigger]);
  const minError = min ? errors.min?.message : null;
  const maxError = max ? errors.max?.message : null;
  const errorMessage = minError ?? maxError;
  return <Form noValidate onSubmit={onSubmit}>
        <FilterPanel content={<FlexColumn className="gap-4">
              <Controller control={control} name="currency" render={({
        field
      }) => <Select disabled={currencyOptions.length === 1} onValueChange={field.onChange} value={field.value}>
                    {currencyOptions.map(c => <SelectItem key={c} value={c}>
                        <SelectItemContent>
                          <SelectItemTitle>{c}</SelectItemTitle>
                        </SelectItemContent>
                      </SelectItem>)}
                  </Select>} />

              <FlexCenter>
                <Input className="w-full" error={Boolean(minError)} min={minimumPrice} placeholder={minimumPrice ? minimumPrice.toString() : t("min")} size="sm" type="number" {...register("min", {
          setValueAs: value => value !== "" ? Number.parseFloat(value) : undefined
        })} />
                <TextBody className="mx-3" weight="semibold">
                  {t("to")}
                </TextBody>
                <Input className="w-full" error={Boolean(maxError)} placeholder={t("max")} size="sm" type="number" {...register("max", {
          setValueAs: value => value !== "" ? Number.parseFloat(value) : undefined
        })} />
              </FlexCenter>

              {errorMessage ? <TextBody color="error-1" size="sm">
                  {errorMessage}
                </TextBody> : null}

              <Button disabled={isFormSubmitDisabled(formState)} size="sm" type="submit" variant="secondary">
                {t("apply")}
              </Button>
            </FlexColumn>} defaultOpen={defaultOpen} disabled={disabled} onOpenChange={onOpenChange} open={open}>
          {title ?? t("price")}
        </FilterPanel>
      </Form>;
}, {
  fallback: PricePanelSkeleton
});
export function PricePanelSkeleton(t0) {
  const $ = _c(19);
  const {
    title,
    open
  } = t0;
  const t = useTranslations("PricePanel");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <SkeletonLine className="h-8" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <SkeletonLine className="h-8 w-full" />;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== t) {
    t3 = t("to");
    $[2] = t;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t3) {
    t4 = <TextBody className="mx-3" weight="semibold">{t3}</TextBody>;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <SkeletonLine className="h-8 w-full" />;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t4) {
    t6 = <Flex>{t2}{t4}{t5}</Flex>;
    $[7] = t4;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <SkeletonLine className="h-8" />;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t6) {
    t8 = <FlexColumn className="gap-4">{t1}{t6}{t7}</FlexColumn>;
    $[10] = t6;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== t || $[13] !== title) {
    t9 = title ?? t("price");
    $[12] = t;
    $[13] = title;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== open || $[16] !== t8 || $[17] !== t9) {
    t10 = <FilterPanel content={t8} open={open}>{t9}</FilterPanel>;
    $[15] = open;
    $[16] = t8;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  return t10;
}